import { useState } from "preact/hooks";
import { logout } from "../api/client.ts";
import type { SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { ignore } from "../lib/ignore.ts";
import { spaceUrl } from "../lib/paths.ts";
import { atLeast } from "../lib/roles.ts";
import { navigate } from "../lib/router.ts";
import { me } from "../lib/state.ts";
import { NewSpaceDialog } from "../pages/settings/newSpace.tsx";
import { OrgLogo } from "../ui/Avatar.tsx";
import { ChooserIcon } from "../ui/icons.tsx";
import { Menu } from "../ui/Menu.tsx";

/**
 * The space switcher: this org's spaces (and a new one, for admins), your other orgs, your organizations, sign out.
 * `onNavigate` hears of going elsewhere (the sidebar's menu, on a phone, closes).
 */
export function SpaceMenu({ space, onNavigate }: { space: SpaceOut; onNavigate: () => void }) {
  const [adding, setAdding] = useState(false);
  const go = (to: string) => {
    onNavigate();
    navigate(to);
  };
  const triage = (id: string) => spaceUrl({ id }, "/triage");
  const orgs = me.value?.orgs ?? [];
  const here = orgs.find((o) => o.org === space.org);
  const spaces = (here?.spaces ?? []).map((s) => ({
    key: s.space,
    label: s.name,
    group: space.org_name,
    current: s.space === space.id,
    onSelect: () => go(triage(s.space)),
  }));
  const add = atLeast(space.role, "admin")
    ? [
        {
          key: "new-space",
          label: t("nav.newSpace"),
          group: space.org_name,
          onSelect: () => setAdding(true),
        },
      ]
    : [];
  const others = orgs.flatMap((o) => {
    const first = o.spaces[0];
    return o.org !== space.org && first
      ? [
          {
            key: o.org,
            label: o.name,
            group: t("nav.otherOrgs"),
            onSelect: () => go(triage(first.space)),
            ...(o.logo && { icon: <OrgLogo id={o.org} name={o.name} logo={o.logo} size={16} /> }),
          },
        ]
      : [];
  });
  const signOut = () => {
    logout().catch(ignore);
  };
  return (
    <>
      <Menu
        label={
          <>
            {here?.logo ? <OrgLogo id={space.org} name={space.org_name} logo={here.logo} size={28} /> : null}
            <span class="min-w-0 flex-1">
              {space.org_name === space.name ? null : (
                <span class="block truncate text-xs text-zinc-500">{space.org_name}</span>
              )}
              <span class="block truncate font-semibold">{space.name}</span>
            </span>
            <ChooserIcon class="text-zinc-500" />
          </>
        }
        class="flex w-full items-center justify-between gap-2 rounded-md border border-zinc-300/80 bg-white/70 px-3 py-2 text-left text-sm shadow-xs hover:bg-white focus-visible:outline-2 focus-visible:outline-blue-600"
        matchWidth={true}
        items={[
          ...spaces,
          ...add,
          ...others,
          { label: t("nav.newOrg"), onSelect: () => go("/profile/orgs") },
          { label: t("nav.signOut"), onSelect: signOut },
        ]}
      />
      <NewSpaceDialog space={space} open={adding} onClose={() => setAdding(false)} />
    </>
  );
}
